'use client';

import { useGetBasketQuery } from '@akinon/next/data/client/basket';
import { getCookie, setCookie } from '@akinon/next/utils';
import { ROUTES } from '@theme/routes';
import { getCsrfToken } from '@theme/utils';
import { md5Hash, pushUserData } from '@theme/utils/gtm';
import { useSession } from 'next-auth/react';
import { usePathname } from 'next/navigation';
import { useEffect } from 'react';

interface GlobalProps {
  pageName?: string;
  productPk?: number;
  pageType?: string;
  pageCategory?: string;
  vpCategory?: string;
  emarsysCategory?: string;
  vl_label?: string;
  vl_category_id?: number;
}

export const GlobalDataLayer = ({
  pageName,
  productPk,
  pageType,
  pageCategory,
  emarsysCategory,
  vl_label,
  vl_category_id
}: GlobalProps) => {
  const pathName = usePathname();
  const { data: sessionData, status } = useSession();
  const { data: basket, isSuccess } = useGetBasketQuery();

  useEffect(() => {
    if (emarsysCategory) {
      window?.dataLayer?.push({
        cd_EmarsysCategory: emarsysCategory || ''
      });
    }

    window?.dataLayer?.push({
      cd_pageType: pageType || '',
      cd_pageCategory: pageCategory || ''
    });

    if (pathName === ROUTES.HOME) {
      window.dataLayer.push({
        event: 'vlEvent',
        vl_label: 'VL-SendMemberType',
        vl_memberType: (sessionData?.user as any)?.attributes?.member_type
          ? (sessionData?.user as any)?.attributes?.member_type
          : ''
      });
    }

    if (vl_category_id && vl_label) {
      window.dataLayer.push({
        event: 'vlEvent',
        vl_label,
        vl_category_id
      });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (window) {
      window.GLOBALS = {
        page: pageName ?? pageType ?? '',
        pageType: pageType ?? '',
        next_url: pathName ?? '',
        userLoggedIn: sessionData?.user ? true : false,
        csrf_token: getCsrfToken(),
        userEmail: sessionData?.user?.email ? sessionData?.user?.email : '',
        userPhone: sessionData?.user?.phone ? sessionData?.user?.phone : '',
        userId: sessionData?.user?.pk ? sessionData?.user?.pk?.toString() : '',
        hashedEmail: md5Hash(sessionData?.user?.email),
        hashedPhone: md5Hash(sessionData?.user?.phone),
        gender: sessionData?.user?.gender,
        customerID: (sessionData?.user as any)?.attributes?.customer_id || undefined,
        userSapId: (sessionData?.user as any)?.attributes?.sap_id || '',
        member_type: sessionData?.user?.attributes?.member_type || '',
        rfm_segment: sessionData?.user?.attributes?.rfm_segment || '',
      };

      if (sessionData && sessionData?.user) {
        window.GLOBALS.firstName = md5Hash(sessionData?.user?.firstName);
        window.GLOBALS.lastName = md5Hash(sessionData?.user?.lastName);
        window.GLOBALS.dateOfBirth = md5Hash(sessionData?.user?.dateOfBirth);
        window.GLOBALS.gender = md5Hash(sessionData?.user?.gender);
      }

      if (productPk) {
        window.GLOBALS.productPk = productPk;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionData, pathName]);

  useEffect(() => {
    if (status === 'loading' || !isSuccess) return;

    // 1. user_data
    if (sessionData?.user?.pk) {
      pushUserData(sessionData?.user);
    }

    // 2. cartInfo
    if (isSuccess) {
      window?.dataLayer?.push({
        cartItems: basket?.basketitem_set?.map((item) => ({
          itemgroup_id: item?.product?.base_code,
          item: item?.product?.sku,
          price: +item?.price,
          quantity: item.quantity
        })),
        event: 'cartInfo'
      });
    }

    // 3. virtualPageView
    if (
      window?.location?.href &&
      document?.title
    ) {
      window?.dataLayer?.push({
        event: 'virtualPageView',
        pageUrl: window?.location?.href || '',
        pageTitle: document?.title || '',
        page_referrer: getCookie('referrer') || '',
        page_category: pageCategory || ''
      });
      setCookie('referrer', window?.location?.href);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathName, status, isSuccess]);

  return null;
};
